import * as React from "react";
import { useState, useEffect } from "react";
import AntDTable from "@/common/components/ui/antd-table";
import { Card, CardContent } from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Download,
  RefreshCw,
  Truck,
} from "lucide-react";
import { GoldService } from "@/services/APIs";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import DeleteConfirmationModal from "@/projects/FinEaseHomeAndAdmin/components/DeleteConfirmationModal";
import ImportDeliveryMasterModal from "./components/ImportDeliveryMasterModal";
import * as XLSX from "xlsx";
import { notify } from "@/common/components/common/Notification";

const DeliveryMaster = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [courierPartnerFilter, setCourierPartnerFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const navigate = useNavigate();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any | null>(null);

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const { toast } = useToast();

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 400); // Small debounce for search
    return () => clearTimeout(timer);
  }, [courierPartnerFilter, currentPage, pageSize, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response: any = await GoldService.listDeliveryMasters(
        courierPartnerFilter,
        currentPage,
        pageSize,
        searchTerm
      );
      const listData = response?.results ?? [];
      setData(listData);
      setTotalCount(response?.count ?? 0);
      setLoading(false);
    } catch (_error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch Delivery Masters",
      });
      // Fallback empty data for UI development if API is missing
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const onSelectChange = (keys: React.Key[], rows: any[]) => {
    setSelectedRowKeys(keys);
    setSelectedRows(rows);
  };

  const handleExport = () => {
    if (selectedRows.length === 0) {
      notify.error("No Data", "Please select items to export.");
      return;
    }

    const rows = selectedRows.map(item => ({
      Partner: item.priority_display ?? "-",
      Pincode: item.pincode ?? "-",
      City: item.bvc_delivery_city ?? item.bd_city ?? "-",
      State: item.bvc_state ?? item.bd_state ?? "-",
      Region: item.bvc_region ?? item.bd_region ?? "-",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "DeliveryMaster");

    XLSX.writeFile(
      wb,
      `delivery_master_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    notify.success("Exported", `${rows.length} record(s) exported.`);
  };

  const handleDelete = async () => {
    if (!itemToDelete) {
      return;
    }
    setIsDeleting(true);
    try {
      const response = await GoldService.deleteDeliveryMaster(itemToDelete.id);
      if (response.success || response) {
        toast({
          title: "Success",
          description: "Record deleted successfully",
        });
        fetchData();
        setIsDeleteDialogOpen(false);
        setItemToDelete(null);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message ?? "An unexpected error occurred",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // No need to frontend filter if backend handles search via API,
  // replacing filteredData with data to prevent breaking pagination.

  const renderDash = (val: any) =>
    val === null || val === undefined || val === "" ? (
      <div className="text-center">-</div>
    ) : (
      val
    );

  const getDynamicColumns = () => {
    const baseColumns = [
      {
        title: "Partner",
        dataIndex: "priority_display",
        key: "priority_display",
        render: (partner: string) =>
          partner ? (
            <span className="font-bold text-secondary">{partner}</span>
          ) : (
            <div className="text-center">-</div>
          ),
      },
      {
        title: "Pincode",
        dataIndex: "pincode",
        key: "pincode",
        render: renderDash,
      },
    ];

    const actionsColumn = {
      title: "Actions",
      key: "actions",
      align: "right" as const,
      fixed: "right" as const,
      // Fixed width to ensure it nicely anchors on the right when scrolling wide tables
      width: 120,
      render: (_: any, record: any) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary hover:bg-primary/10 hover:text-primary"
            onClick={() =>
              navigate(
                finEaseHomeNavigationRoutes.PHYSICAL_GOLD_VIEW_DELIVERY_MASTER(
                  record.id
                )
              )
            }
            title="View"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary hover:bg-primary/10 hover:text-primary"
            onClick={() =>
              navigate(
                finEaseHomeNavigationRoutes.PHYSICAL_GOLD_EDIT_DELIVERY_MASTER(
                  record.id
                )
              )
            }
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => {
              setItemToDelete(record);
              setIsDeleteDialogOpen(true);
            }}
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    };

    if (courierPartnerFilter === "1") {
      return [
        ...baseColumns,
        {
          title: "State",
          dataIndex: "bvc_state",
          key: "state",
          render: renderDash,
        },
        {
          title: "Region",
          dataIndex: "bvc_region",
          key: "region",
          render: renderDash,
        },
        {
          title: "City",
          dataIndex: "bvc_delivery_city",
          key: "city",
          render: renderDash,
        },
        {
          title: "Delivery Loc",
          dataIndex: "bvc_delivery_loc",
          key: "bvc_delivery_loc",
          render: renderDash,
        },
        {
          title: "Area Name",
          dataIndex: "bvc_delivery_area_name",
          key: "bvc_delivery_area_name",
          render: renderDash,
        },
        {
          title: "Service Days",
          dataIndex: "bvc_service_days",
          key: "service_days",
          render: renderDash,
        },
        {
          title: "Pickup",
          dataIndex: "bvc_pickup_service",
          key: "bvc_pickup_service",
          render: renderDash,
        },
        {
          title: "Delivery",
          dataIndex: "bvc_delivery_service",
          key: "bvc_delivery_service",
          render: renderDash,
        },
        {
          title: "COD Delivery",
          dataIndex: "bvc_cod_delivery_service",
          key: "bvc_cod_delivery_service",
          render: renderDash,
        },
        {
          title: "Reverse Pickup",
          dataIndex: "bvc_reverse_pickup_service",
          key: "bvc_reverse_pickup_service",
          render: renderDash,
        },
        actionsColumn,
      ];
    }

    if (courierPartnerFilter === "2") {
      return [
        ...baseColumns,
        {
          title: "State",
          dataIndex: "bd_state",
          key: "bd_state",
          render: renderDash,
        },
        {
          title: "Region",
          dataIndex: "bd_region",
          key: "bd_region",
          render: renderDash,
        },
        {
          title: "City",
          dataIndex: "bd_city",
          key: "bd_city",
          render: renderDash,
        },
        {
          title: "CAREA",
          dataIndex: "bd_carea",
          key: "bd_carea",
          render: renderDash,
        },
        {
          title: "CSCRCD",
          dataIndex: "bd_cscrcd",
          key: "bd_cscrcd",
          render: renderDash,
        },
        {
          title: "CAREADESC",
          dataIndex: "bd_careadesc",
          key: "bd_careadesc",
          render: renderDash,
        },
        {
          title: "BDEL_LOC",
          dataIndex: "bd_bdel_loc",
          key: "bd_bdel_loc",
          render: renderDash,
        },
        {
          title: "CSTATECD",
          dataIndex: "bd_cstatecd",
          key: "bd_cstatecd",
          render: renderDash,
        },
        {
          title: "GSTCODE",
          dataIndex: "bd_gstcode",
          key: "bd_gstcode",
          render: renderDash,
        },
        {
          title: "ZONE",
          dataIndex: "bd_zone",
          key: "bd_zone",
          render: renderDash,
        },
        {
          title: "CLOCTYPE",
          dataIndex: "bd_cloctype",
          key: "bd_cloctype",
          render: renderDash,
        },
        {
          title: "DSTARCD",
          dataIndex: "bd_dstarcd",
          key: "bd_dstarcd",
          render: renderDash,
        },
        {
          title: "CCRCRDSCR",
          dataIndex: "bd_ccrcrdscr",
          key: "bd_ccrcrdscr",
          render: renderDash,
        },
        {
          title: "CSFCZONE",
          dataIndex: "bd_csfczone",
          key: "bd_csfczone",
          render: renderDash,
        },
        {
          title: "SFCZONEDESC",
          dataIndex: "bd_sfczonedesc",
          key: "bd_sfczonedesc",
          render: renderDash,
        },
        {
          title: "SUBREGION",
          dataIndex: "bd_subregion",
          key: "bd_subregion",
          render: renderDash,
        },
        {
          title: "CSERVFLAG",
          dataIndex: "bd_cservflag",
          key: "bd_cservflag",
          render: renderDash,
        },
        {
          title: "CTEL",
          dataIndex: "bd_ctel",
          key: "bd_ctel",
          render: renderDash,
        },
        {
          title: "CNT",
          dataIndex: "bd_cnt",
          key: "bd_cnt",
          render: renderDash,
        },
        {
          title: "NEWZONE",
          dataIndex: "bd_newzone",
          key: "bd_newzone",
          render: renderDash,
        },
        {
          title: "DOD_APEX",
          dataIndex: "bd_dod_apex",
          key: "bd_dod_apex",
          render: renderDash,
        },
        {
          title: "DOD_SFC",
          dataIndex: "bd_dod_sfc",
          key: "bd_dod_sfc",
          render: renderDash,
        },
        {
          title: "FOD_APEX",
          dataIndex: "bd_fod_apex",
          key: "bd_fod_apex",
          render: renderDash,
        },
        {
          title: "FOD_SFC",
          dataIndex: "bd_fod_sfc",
          key: "bd_fod_sfc",
          render: renderDash,
        },
        {
          title: "FORM",
          dataIndex: "bd_form",
          key: "bd_form",
          render: renderDash,
        },
        {
          title: "OCTROI",
          dataIndex: "bd_octroi",
          key: "bd_octroi",
          render: renderDash,
        },
        {
          title: "NDPDVAL",
          dataIndex: "bd_ndpdval",
          key: "bd_ndpdval",
          render: renderDash,
        },
        {
          title: "INTFLAG",
          dataIndex: "bd_intflag",
          key: "bd_intflag",
          render: renderDash,
        },
        {
          title: "OTMDEL",
          dataIndex: "bd_otmdel",
          key: "bd_otmdel",
          render: renderDash,
        },
        {
          title: "CEBAGDST",
          dataIndex: "bd_cebagdst",
          key: "bd_cebagdst",
          render: renderDash,
        },
        {
          title: "DOMESTIC",
          dataIndex: "bd_domestic",
          key: "bd_domestic",
          render: renderDash,
        },
        {
          title: "APEX",
          dataIndex: "bd_apex",
          key: "bd_apex",
          render: renderDash,
        },
        {
          title: "SURFACE",
          dataIndex: "bd_surface",
          key: "bd_surface",
          render: renderDash,
        },
        {
          title: "COD",
          dataIndex: "bd_cod",
          key: "bd_cod",
          render: renderDash,
        },
        {
          title: "CREDITCARD",
          dataIndex: "bd_creditcard",
          key: "bd_creditcard",
          render: renderDash,
        },
        {
          title: "TDD",
          dataIndex: "bd_tdd",
          key: "bd_tdd",
          render: renderDash,
        },
        {
          title: "TDDAPEX1200",
          dataIndex: "bd_tddapex1200",
          key: "bd_tddapex1200",
          render: renderDash,
        },
        actionsColumn,
      ];
    }

    // Default 'All' view
    return [
      ...baseColumns,
      {
        title: "City",
        key: "city",
        render: (_: any, record: any) =>
          renderDash(record.bvc_delivery_city ?? record.bd_city),
      },
      {
        title: "State",
        key: "state",
        render: (_: any, record: any) =>
          renderDash(record.bvc_state ?? record.bd_state),
      },
      {
        title: "Region",
        key: "region",
        render: (_: any, record: any) =>
          renderDash(record.bvc_region ?? record.bd_region),
      },
      actionsColumn,
    ];
  };

  return (
    <div className="space-y-6 p-6 pb-20">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <Truck className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">
                Delivery Master
              </h1>
              <p className="text-white/70">
                Create, edit, and manage all Delivery Pincode Records
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />{" "}
              Sync Data
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={handleExport}
              disabled={selectedRows.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
              {selectedRows.length > 0 && (
                <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                  {selectedRows.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      <Card className="border-none shadow-sm">
        <CardContent className="flex flex-col justify-between gap-4 p-4 md:flex-row md:items-center">
          <div className="flex flex-1 gap-4">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by pincode or city..."
                className="border-gray-100 bg-gray-50/50 pl-9 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset page on search
                }}
              />
            </div>
            <div className="w-[200px]">
              <Select
                value={courierPartnerFilter}
                onValueChange={v => {
                  setCourierPartnerFilter(v);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Courier Partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Partners</SelectItem>
                  <SelectItem value="1">BVC</SelectItem>
                  <SelectItem value="2">Bluedart</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              className="gap-2 shadow-lg shadow-primary/20"
              onClick={() =>
                navigate(
                  finEaseHomeNavigationRoutes.PHYSICAL_GOLD_ADD_DELIVERY_MASTER
                )
              }
            >
              <Plus className="h-4 w-4" /> Add Record
            </Button>
          </div>
        </CardContent>
      </Card>

      <AntDTable
        showSettings
        columns={getDynamicColumns()}
        data={data}
        rowKey="id"
        loading={loading}
        rowSelection={{
          selectedRowKeys,
          onChange: onSelectChange,
        }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Record"
        message="Are you sure you want to delete this delivery record?"
        itemName={itemToDelete?.pincode ?? ""}
        isLoading={isDeleting}
      />

      {isImportModalOpen && (
        <ImportDeliveryMasterModal
          isOpen={isImportModalOpen}
          onClose={() => {
            setIsImportModalOpen(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default DeliveryMaster;
